import { useState, useEffect } from 'react';
import api from '@/api';
import { formatLabel, BADGE, timeAgo } from '@/lib/format';
import { Plus, PencilSimple, Key, UserMinus, Copy, Eye, EyeSlash, Info } from '@phosphor-icons/react';

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full system access' },
  { value: 'compliance_manager', label: 'Compliance Manager', desc: 'Cases, screening, monitoring, reports, data sync' },
  { value: 'analyst', label: 'Analyst', desc: 'Cases, screening, reports' },
  { value: 'read_only', label: 'Read Only', desc: 'View access only' },
];

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  useEffect(() => { document.title = "Radar — Users"; }, []);
  const [showCreate, setShowCreate] = useState(false);
  const [showSuccess, setShowSuccess] = useState(null);
  const [showReset, setShowReset] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', role: 'analyst', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');

  const fetchUsers = () => api.get('/admin/users').then(r => setUsers(r.data.users)).catch(() => {});
  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async () => {
    setError('');
    if (!form.name.trim() || form.name.length < 2) { setError('Name must be at least 2 characters'); return; }
    if (!form.email.includes('@')) { setError('Valid email required'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    try {
      await api.post('/admin/users', form);
      setShowCreate(false);
      setShowSuccess({ name: form.name, email: form.email.toLowerCase(), role: form.role, password: form.password });
      setForm({ name: '', email: '', role: 'analyst', password: '' });
      fetchUsers();
    } catch (e) { setError(e.response?.data?.detail || 'Failed to create user'); }
  };

  const handleDeactivate = async (userId, email, currentActive) => {
    if (!window.confirm(`${currentActive ? 'Deactivate' : 'Reactivate'} user ${email}?`)) return;
    try { await api.post(`/admin/users/${userId}/deactivate`); fetchUsers(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleResetPassword = async (userId) => {
    const pw = showReset?.newPassword;
    if (!pw || pw.length < 8) { alert('Password must be at least 8 characters'); return; }
    try { await api.post(`/admin/users/${userId}/reset-password`, { password: pw }); setShowReset(null); alert('Password reset successfully'); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const copyCredentials = (email, password) => {
    navigator.clipboard.writeText(`Email: ${email} | Password: ${password}`);
    alert('Credentials copied to clipboard');
  };

  const pwStrength = (pw) => {
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^a-zA-Z0-9]/.test(pw)) s++;
    return ['Weak', 'Fair', 'Medium', 'Strong'][Math.min(s, 3)];
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-2xl font-bold text-[#0A0A0A]">User Management</h1><p className="text-sm text-[#52525B]">Users are synchronized from Tychi Master</p></div>
      </div>

      <div
        className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-md px-4 py-3 mb-4"
        data-testid="users-source-banner"
      >
        <Info size={18} className="text-blue-700 shrink-0 mt-0.5" />
        <p className="text-xs text-blue-800 leading-relaxed">
          <strong>Users are managed in Tychi Master.</strong> To add, edit role, or remove users,
          please use the Tychi Master console — changes will sync automatically. You can
          deactivate or re-activate access for emergencies below.
        </p>
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-[#E4E4E7] bg-[#F7F7F8]">
            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Name</th>
            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Email</th>
            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Role</th>
            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Status</th>
            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Created</th>
            <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-[#52525B]">Actions</th>
          </tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-[#E4E4E7] hover:bg-[#F7F7F8]">
                <td className="px-4 py-3 text-sm font-medium">{u.name}</td>
                <td className="px-4 py-3 text-sm text-[#52525B]">{u.email}</td>
                <td className="px-4 py-3"><span className={`${BADGE} bg-blue-50 text-blue-700 border-blue-200`}>{formatLabel(u.role)}</span></td>
                <td className="px-4 py-3"><span className={`${BADGE} ${u.is_active !== false ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{u.is_active !== false ? 'Active' : 'Deactivated'}</span></td>
                <td className="px-4 py-3 text-xs text-[#A1A1AA]">{timeAgo(u.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => setShowReset({ id: u.id, email: u.email, newPassword: '' })} className="px-2 py-1 text-xs border border-[#E4E4E7] rounded hover:bg-[#F0F0F1] flex items-center gap-1" title="Reset Password"><Key size={12} /> Reset</button>
                    <button onClick={() => handleDeactivate(u.id, u.email, u.is_active !== false)} className="px-2 py-1 text-xs border border-[#E4E4E7] rounded hover:bg-[#F0F0F1] flex items-center gap-1" title={u.is_active !== false ? 'Deactivate' : 'Reactivate'}><UserMinus size={12} /> {u.is_active !== false ? 'Deactivate' : 'Activate'}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg border border-[#E4E4E7] shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-semibold mb-4">Create New User</h2>
            {error && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">{error}</div>}
            <div className="space-y-3">
              <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Full Name *</label>
                <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" /></div>
              <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Email Address *</label>
                <input value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} type="email" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" /></div>
              <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Role *</label>
                <select value={form.role} onChange={e => setForm(p => ({...p, role: e.target.value}))} className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md">
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
                </select></div>
              <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Password *</label>
                <div className="relative mt-1">
                  <input value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} type={showPw ? 'text' : 'password'} className="w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md pr-10" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A1A1AA]">{showPw ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-[#A1A1AA]">Min 8 chars, 1 uppercase, 1 number</span>
                  {form.password && <span className={`text-[10px] font-medium ${pwStrength(form.password) === 'Strong' ? 'text-green-600' : pwStrength(form.password) === 'Medium' ? 'text-yellow-600' : 'text-red-600'}`}>{pwStrength(form.password)}</span>}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]">Cancel</button>
              <button onClick={handleCreate} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">Create User</button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg border border-[#E4E4E7] shadow-xl w-full max-w-md p-6">
            <h2 className="font-heading text-lg font-semibold mb-3 text-green-700">✅ User Created Successfully</h2>
            <div className="space-y-2 text-sm bg-[#F7F7F8] rounded-md p-4 mb-4">
              <div><span className="text-[#52525B]">Name:</span> <strong>{showSuccess.name}</strong></div>
              <div><span className="text-[#52525B]">Email:</span> <strong>{showSuccess.email}</strong></div>
              <div><span className="text-[#52525B]">Role:</span> <strong>{formatLabel(showSuccess.role)}</strong></div>
              <div><span className="text-[#52525B]">Password:</span> <strong className="font-mono">{showSuccess.password}</strong></div>
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded mb-4">⚠️ Share these credentials securely. The user should change their password after first login.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => copyCredentials(showSuccess.email, showSuccess.password)} className="flex items-center gap-1 px-4 py-2 text-sm border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]"><Copy size={14} /> Copy Credentials</button>
              <button onClick={() => setShowSuccess(null)} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showReset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowReset(null)}>
          <div className="bg-white rounded-lg border border-[#E4E4E7] shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h2 className="font-heading text-base font-semibold mb-3">Reset Password — {showReset.email}</h2>
            <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">New Password</label>
              <input value={showReset.newPassword} onChange={e => setShowReset(p => ({...p, newPassword: e.target.value}))} type="text" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md font-mono" /></div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowReset(null)} className="px-3 py-1.5 text-sm border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]">Cancel</button>
              <button onClick={() => handleResetPassword(showReset.id)} className="px-3 py-1.5 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">Reset Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
